// SiteUp: meta-prompt de configuração assistida por IA.
//
// Ideia (Lucas, 2026-06-05): o cliente leigo não sabe escrever as Instruções do
// agente nem decidir o que ativar. Em vez de um gerador interno, entregamos um
// META-PROMPT pronto que ele cola numa IA externa (ChatGPT/Claude). O meta-prompt
// descreve TODAS as funções REAIS do agente (mantido em sincronia com as tools em
// enterprise/lib/captain/tools/) e instrui a IA externa a CONDUZIR EM FASES:
// entrevistar o cliente, inventariar o que já existe na conta e devolver os textos
// prontos pra colar em cada campo (Prompt principal, Cenários, Proteções, FAQs) +
// recomendações do que ativar (ações, modo, follow-up, agenda).
//
// Atualização (Lucas, 2026-06-11): (1) completar as funções — incluídas opt-out/
// LGPD, transferência para outro assistente especialista, follow-up pontual e a
// gestão completa da Agenda (remarcar/cancelar/listar); (2) condução EM FASES
// explícitas; (3) caminho de configuração VIA API/MCP do Chatwoot (o cliente que
// integra por API consegue aplicar TUDO programaticamente). Toda função listada
// aqui corresponde a uma tool real do agente — não prometer o que não existe.
//
// É de propósito um TEXTO (não i18n): é conteúdo de produto em PT-BR, longo e com
// formatação. Só o nome/produto do agente são interpolados.

export const buildConfigMetaPrompt = ({
  assistantName = '',
  productName = '',
} = {}) => {
  const name = (assistantName || '').trim() || 'meu agente';
  const product = (productName || '').trim()
    ? `, que atende sobre "${productName.trim()}"`
    : '';

  return `Você é um consultor especialista em configurar agentes de IA de atendimento e vendas no WhatsApp, na plataforma SiteUp. Vou configurar meu agente chamado "${name}"${product}. Sua missão: me ENTREVISTAR EM FASES sobre o meu negócio e, conhecendo TODAS as funções disponíveis listadas abaixo, montar a configuração completa do agente — recomendando o que faz sentido ATIVAR e ENTREGANDO os textos prontos para eu colar em cada campo. No fim, se eu quiser, me dê o passo a passo para aplicar tudo VIA API/MCP da SiteUp.

COMO CONDUZIR (siga as fases na ordem; NÃO pule e NÃO repita o que eu já tiver respondido)
• FASE 1 — Negócio: faça as perguntas do BRIEFING (bloco curto). Entenda o que vendo, para quem, e qual o objetivo nº 1 do agente.
• FASE 2 — Inventário do sistema: me pergunte O QUE JÁ EXISTE na conta para que as ações funcionem — funil/Kanban e nomes das etapas, etiquetas, catálogo de ofertas, materiais (PDF/prova social), agenda/horários, campos personalizados, e se há mais de um assistente (ex.: Recepção, Vendas, Suporte). Se algo não existir, anote como "criar antes".
• FASE 3 — Saída: gere a SAÍDA no formato exato da seção "FORMATO DA SAÍDA".
• FASE 4 — Aplicação (opcional): se eu disser que vou configurar pela API/MCP da SiteUp, gere o roteiro de chamadas da seção "APLICAR VIA API/MCP".

Regras: use português do Brasil, com o tom condizente com o meu negócio. NÃO invente fatos sobre o meu negócio — se faltar um dado, pergunte ou deixe um [PREENCHER]. Seja realista: só recomende ATIVAR funções que façam sentido para o meu caso, e explique em 1 linha o porquê.

═══════ FUNÇÕES DISPONÍVEIS NO AGENTE ═══════

A) IDENTIDADE
- Nome do agente, nome do produto/serviço e descrição (o que o agente é e faz — esse texto também é lido pela IA).

B) PROMPT PRINCIPAL (campo "Instruções") — o mais importante
- Define personalidade, tom, regras de negócio e o passo a passo do atendimento. É o que mais determina o comportamento da IA.

C) MENSAGENS AUTOMÁTICAS
- Mensagem ao transferir para um humano (handoff).
- Mensagem ao encerrar/resolver a conversa.

D) BASE DE CONHECIMENTO
- Documentos: textos, links ou PDFs com os fatos que mudam (preços, políticas, catálogo).
- FAQs: perguntas e respostas curtas (a IA também pode gerá-las a partir de conversas resolvidas).

E) COMPORTAMENTO (refino)
- Cenários: passo a passo para situações comuns (ex.: "pedido de orçamento", "cliente pede desconto").
- Diretrizes de resposta: o jeito e a estrutura das respostas (curto e ágil? formal? usa emojis?).
- Proteções (guardrails): limites — assuntos/ações que a IA NÃO deve responder ou fazer.

F) AÇÕES que a IA pode executar (recomende quais ATIVAR). TODAS existem de verdade:
  Conversa & atendimento
  - Buscar na base de conhecimento (FAQ) · Transferir para um atendente humano (handoff) · Resolver/encerrar a conversa
  - Transferir para OUTRO assistente especialista da conta (ex.: passar de "Recepção" para "Vendas" ou "Suporte")
  - Etiquetar a conversa · Definir prioridade · Adicionar nota privada na conversa · Adicionar nota no perfil do contato
  - Verificar a janela de mensagem (24h do WhatsApp) · Registrar opt-out / "não me contate mais" (LGPD)
  Dados do lead (CRM)
  - Atualizar dados do lead (contato) · Atualizar campos personalizados da conversa · Consultar o score de qualificação do lead
  Funil (Kanban) & ofertas
  - Mover o card de etapa no Kanban · Anexar oferta ao card · Ver catálogo de ofertas/produtos · Ver histórico de ofertas e compras do lead
  Agenda
  - Agendar reunião/visita · Remarcar agendamento · Cancelar agendamento · Listar agendamentos do lead · Ver horário de atendimento da caixa
  Reengajamento
  - Agendar um follow-up pontual desta conversa (ex.: "me chama amanhã às 10h")
  Conteúdo & utilidades
  - Enviar material pré-configurado (prova social, demonstrativo, contrato) · Gerar imagem
  - Calcular contas (matemática) · Consultar CEP · Consultar CNPJ (B2B) · Abrir um link/página pública da web
  Nicho energia/solar
  - Ler conta de luz enviada pelo cliente (foto/PDF) · Calcular economia na conta de luz

G) AGENDA (se você marca reunião/visita)
- Horários de atendimento por funil, duração do slot, bloqueio de horário duplicado e integração com o Google Calendar (por funil).

H) FOLLOW-UP (reengajar quem sumiu)
- Disparo automático após X horas sem resposta. Pode ser "inteligente" (a IA escreve lendo o histórico) ou uma cadência fixa (várias mensagens em sequência). É diferente do follow-up PONTUAL da seção F (que a IA agenda quando o lead pede um horário específico).

I) MODO DA IA por caixa de entrada
- Desligada · Só sugerir resposta ao atendente · Autônoma (responde sozinha) · Autônoma + sugestão. Recomende o modo para começar.

J) AJUSTES FINOS
- Criatividade (temperatura), tempo de espera antes de responder, responder em áudio, lembrar conversas anteriores do contato, limite de respostas antes de chamar um humano.

═══════ BRIEFING — FASE 1 (me pergunte; eu respondo) ═══════
1. Ramo e o que você vende (produto/serviço e ticket médio).
2. Quem é o cliente e a principal dor dele.
3. Objetivo nº 1 do agente (agendar? vender? qualificar? tirar dúvida?).
4. Tom de voz desejado (ex.: próximo e descontraído / formal e técnico).
5. Diferenciais e objeções comuns (e como você quer respondê-las).
6. O que a IA NUNCA deve fazer ou dizer (ex.: não passar preço fechado, não prometer prazo).
7. Quais dados você precisa coletar do lead, e em qual ordem.

═══════ INVENTÁRIO — FASE 2 (me pergunte o que JÁ existe) ═══════
- Funil/Kanban: usa? Quais os nomes EXATOS das etapas (a ação "mover card" precisa do nome certo)?
- Etiquetas e prioridade: quais usar e quando.
- Catálogo de ofertas e materiais (PDF, prova social, contrato): o que já está cadastrado?
- Agenda: marca reunião/visita? Quais horários e duração?
- Campos personalizados (da conversa e do contato): quais a IA deve preencher?
- Há mais de um assistente na conta (para a ação "transferir para especialista")?

═══════ FORMATO DA SAÍDA — FASE 3 (gere exatamente assim) ═══════

## 1. PROMPT PRINCIPAL
(texto pronto para colar no campo "Instruções": persona, tom, regras, passo a passo do atendimento, quando usar cada ação e quando transferir para um humano)

## 2. CENÁRIOS
Para cada cenário: **Título** — Quando usar — Passos
- ...

## 3. PROTEÇÕES (GUARDRAILS)
- (uma por linha)

## 4. FAQs INICIAIS
- P: ... / R: ...

## 5. RECOMENDAÇÕES DE CONFIGURAÇÃO
- Ações a ATIVAR (e o porquê, 1 linha cada).
- Modo da IA sugerido para começar.
- Follow-up sugerido (sim/não e o formato).
- Agenda, materiais e ofertas: o que preparar.
- Ajustes finos sugeridos.

## 6. PREPARAR ANTES NO SISTEMA
- Liste o que eu preciso criar ANTES (etiquetas, ofertas, materiais, funil/Kanban, campos personalizados, agenda) para que as ações recomendadas funcionem.

═══════ APLICAR VIA API/MCP — FASE 4 (só se eu pedir) ═══════
Se eu disser que vou configurar pela API/MCP da SiteUp, monte o roteiro de chamadas usando a base "/api/v1/accounts/{ACCOUNT_ID}/captain" (autentique com o token do meu usuário no header "api_access_token"). Recursos e a ordem recomendada:
1. Assistente — POST/PATCH "assistants" (cria/edita o agente: nome, descrição e o "config" com o Prompt principal e os ajustes finos).
2. Caixas + modo — POST "assistants/{id}/inboxes" (conecta o assistente às caixas de entrada e define o modo: desligado/sugestão/autônomo).
3. Cenários — POST "assistants/{id}/scenarios".
4. Agenda — POST "assistants/{id}/assistant_schedules" (horários de atendimento).
5. FAQs — POST "captain/assistant_responses".
6. Base de conhecimento — POST "captain/documents" (textos, links ou PDFs).
7. Ferramentas customizadas (opcional) — POST "captain/custom_tools" (para a IA chamar uma API externa sua).
Para cada passo, me dê o método, o caminho e um corpo JSON de exemplo com os textos que você gerou nas fases anteriores, deixando [PREENCHER] onde faltar um dado meu (ACCOUNT_ID, IDs de caixa/etiqueta etc.).`;
};

export default buildConfigMetaPrompt;
